import axios from 'axios'
import { ref, watch } from 'vue'

/**
 * Composable for managing vehicle configurator logic (brands, models, versions, engines).
 *
 * @param {Object} form - Reactive form object containing vehicle fields
 * @param {Object} options - Configuration options
 * @param {Function} options.onError - Error handler callback
 * @param {boolean} options.includeEngines - Whether to include engines (default: true)
 * @param {boolean} options.includeAllOption - Whether to include "All" option in lists (default: false)
 * @param {boolean} options.autoWatch - Whether to enable automatic watchers (default: true)
 * @param {string} options.typeField - Form field name for vehicle type (default: 'vehicle_type')
 * @param {string} options.brandField - Form field name for brand id (default: 'vehicle_brand_id')
 * @param {string} options.modelField - Form field name for model id (default: 'vehicle_model_id')
 * @param {string} options.versionField - Form field name for version id (default: 'vehicle_version_id')
 * @param {string} options.engineField - Form field name for engine id (default: 'vehicle_engine_id')
 */
export function useVehicleConfigurator(form, {
    onError = () => {},
    includeEngines = true,
    includeAllOption = false,
    autoWatch = true,
    typeField = 'vehicle_type',
    brandField = 'vehicle_brand_id',
    modelField = 'vehicle_model_id',
    versionField = 'vehicle_version_id',
    engineField = 'vehicle_engine_id',
} = {}) {
    const brands = ref([])
    const models = ref([])
    const versions = ref([])
    const engines = ref([])
    const isInitialized = ref(false)

    /**
     * Fetches brands from API based on vehicle type.
     */
    const fetchBrands = () => {
        brands.value = []
        if (includeAllOption) {
            brands.value.push({ text: 'Toutes les marques', value: null })
        }
        return new Promise((resolve, reject) => {
            axios.get('/api/chiptuning/brands?type=' + form[typeField])
                .then(response => {
                    response.data.data.forEach(brand => {
                        brands.value.push({
                            text: brand.data.name,
                            value: brand.data.id
                        })
                    })
                    resolve()
                })
                .catch(error => {
                    onError(error)
                    reject(error)
                })
        })
    }

    /**
     * Fetches models from API based on selected brand.
     */
    const fetchModels = () => {
        models.value = []
        if (includeAllOption) {
            models.value.push({ text: 'Tous les modèles', value: null })
        }
        return new Promise((resolve, reject) => {
            axios.get('/api/chiptuning/models?vehicle_brand_id=' + form[brandField])
                .then(response => {
                    response.data.data.forEach(model => {
                        models.value.push({
                            text: model.data.name,
                            value: model.data.id
                        })
                    })
                    resolve()
                })
                .catch(error => {
                    onError(error)
                    reject(error)
                })
        })
    }

    /**
     * Fetches versions from API based on selected model.
     */
    const fetchVersions = () => {
        versions.value = []
        if (includeAllOption) {
            versions.value.push({ text: 'Toutes les versions', value: null })
        }
        return new Promise((resolve, reject) => {
            axios.get('/api/chiptuning/versions?vehicle_model_id=' + form[modelField])
                .then(response => {
                    response.data.data.forEach(version => {
                        versions.value.push({
                            text: version.data.name,
                            value: version.data.id
                        })
                    })
                    resolve()
                })
                .catch(error => {
                    onError(error)
                    reject(error)
                })
        })
    }

    /**
     * Fetches engines from API based on selected version.
     */
    const fetchEngines = () => {
        engines.value = []
        if (includeAllOption) {
            engines.value.push({ text: 'Tous les moteurs', value: null })
        }
        return new Promise((resolve, reject) => {
            axios.get('/api/chiptuning?all=1&vehicle_version_id=' + form[versionField])
                .then(response => {
                    response.data.data.forEach(engine => {
                        engines.value.push({
                            text: engine.data.power_type_label + ' - ' + engine.data.name + ' - ' + engine.data.horsepower + 'ch / ' + engine.data.torque + 'Nm',
                            value: engine.data.id
                        })
                    })
                    resolve()
                })
                .catch(error => {
                    onError(error)
                    reject(error)
                })
        })
    }

    /**
     * Watch vehicle type changes and cascade reset.
     */
    if (autoWatch) {
        watch(
            () => form[typeField],
            async (newValue, oldValue) => {
                if (isInitialized.value && newValue !== oldValue) {
                    form[brandField] = null
                    form[modelField] = null
                    form[versionField] = null
                    if (includeEngines) {
                        form[engineField] = null
                    }
                    brands.value = []
                    models.value = []
                    versions.value = []
                    engines.value = []
                    if (form[typeField] !== null) {
                        await fetchBrands()
                    }
                }
            },
            { deep: true }
        )

        /**
         * Watch brand changes and cascade reset.
         */
        watch(
            () => form[brandField],
            async (newValue, oldValue) => {
                if (isInitialized.value && newValue !== oldValue) {
                    form[modelField] = null
                    form[versionField] = null
                    if (includeEngines) {
                        form[engineField] = null
                    }
                    models.value = []
                    versions.value = []
                    engines.value = []
                    if (form[brandField] !== null) {
                        await fetchModels()
                    }
                }
            },
            { deep: true }
        )

        /**
         * Watch model changes and cascade reset.
         */
        watch(
            () => form[modelField],
            async (newValue, oldValue) => {
                if (isInitialized.value && newValue !== oldValue) {
                    form[versionField] = null
                    if (includeEngines) {
                        form[engineField] = null
                    }
                    versions.value = []
                    engines.value = []
                    if (form[modelField] !== null) {
                        await fetchVersions()
                    }
                }
            },
            { deep: true }
        )

        /**
         * Watch version changes and cascade reset.
         */
        if (includeEngines) {
            watch(
                () => form[versionField],
                async (newValue, oldValue) => {
                    if (isInitialized.value && newValue !== oldValue) {
                        form[engineField] = null
                        engines.value = []
                        if (form[versionField] !== null) {
                            await fetchEngines()
                        }
                    }
                },
                { deep: true }
            )
        }
    }

    /**
     * Initialize the configurator from existing data (edit mode).
     * Sequentially loads each level based on existing form values.
     *
     * @param {Object} data - Data object with vehicle fields
     */
    const initializeFromData = async (data) => {
        if (data[typeField] !== null && data[typeField] !== undefined) {
            form[typeField] = data[typeField]
            await fetchBrands()
        }
        if (data[brandField] !== null && data[brandField] !== undefined) {
            form[brandField] = data[brandField]
            await fetchModels()
        }
        if (data[modelField] !== null && data[modelField] !== undefined) {
            form[modelField] = data[modelField]
            await fetchVersions()
        }
        if (includeEngines && data[versionField] !== null && data[versionField] !== undefined) {
            form[versionField] = data[versionField]
            if (data[engineField] !== null && data[engineField] !== undefined) {
                form[engineField] = data[engineField]
            }
            await fetchEngines()
        } else if (!includeEngines && data[versionField] !== null && data[versionField] !== undefined) {
            form[versionField] = data[versionField]
        }
    }

    /**
     * Reset the configurator to initial state.
     */
    const reset = () => {
        form[typeField] = null
        form[brandField] = null
        form[modelField] = null
        form[versionField] = null
        if (includeEngines) {
            form[engineField] = null
        }
        brands.value = []
        models.value = []
        versions.value = []
        engines.value = []
    }

    /**
     * Set isInitialized to true to enable watchers.
     * Call this after initial data setup is complete.
     */
    const setInitialized = () => {
        isInitialized.value = true
    }

    return {
        brands,
        models,
        versions,
        engines,
        isInitialized,
        fetchBrands,
        fetchModels,
        fetchVersions,
        fetchEngines,
        initializeFromData,
        reset,
        setInitialized,
    }
}
